import { yupResolver } from '@hookform/resolvers/yup';
import { groupBy, merge, orderBy, sortBy, values } from 'lodash';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

import { EquipmentProfileItemDto, StringTranslation } from '@pimm/services/lib/sms-tenants';

export const FormEquipmentItemSchema = yup.object({
  inventoryItemId: yup.string(),
  isCustomerEditable: yup.boolean().optional().default(false),
  isOverridden: yup.boolean().default(false),
  quantityMax: yup.number().default(0),
  quantityMin: yup.number().default(0),
  quantity: yup.number().default(0),
  value: yup.number().default(0),
  title: yup.string().default(''),
});

export const FormEquipmentCategorySchema = yup.object({
  categoryId: yup.number(),
  subPage: yup.string().default(''),
  items: yup.array().of(FormEquipmentItemSchema).default([]),
  lockedItems: yup.number().default(0),
  subTitle: yup.string().default(''),
  subTitleTranslations: yup.mixed<StringTranslation[]>().optional(),
  title: yup.string().default(''),
  titleTranslations: yup.mixed<StringTranslation[]>().optional(),
});

const FormEquipmentProfileSchema = yup.object({
  isEditEnabled: yup.boolean().optional().default(false),
  categories: yup.array().of(FormEquipmentCategorySchema).default([]),
});

export type FormDataEquipmentItem = yup.InferType<typeof FormEquipmentItemSchema>;
export type FormDataEquipmentCategory = yup.InferType<typeof FormEquipmentCategorySchema>;
export type FormEquipmentProfile = yup.InferType<typeof FormEquipmentProfileSchema>;
export type FormEquipmentProfileReturn = UseFormReturn<FormEquipmentProfile>;

export type FormEquipmentProfileProps = {
  defaultValues?: {
    isEditEnabled?: boolean;
    categories?: FormDataEquipmentCategory[];
  };
};

export const transformEquipmentProfileToFormData = (name: string, equipmentProfiles?: EquipmentProfileItemDto[]) => {
  const mapProfilesByPrefix = groupBy(equipmentProfiles, _ => _.key?.split('.')[1] ?? '');
  const sortItemsByTitle = orderBy(mapProfilesByPrefix[name], ['title'], ['asc']);
  const mapItemsByCategory = groupBy(sortItemsByTitle, _ => _.category?.id);
  const sections = sortBy(values(mapItemsByCategory), items => items[0].category?.seq);

  // prettier-ignore
  const categories = sections.filter(_items => !!_items[0]?.category).map(_items => {
      const category = _items[0].category!;

      return {
        categoryId: category.id,
        title: category.title ?? '',
        titleTranslations: category.titleTranslations,
        subTitle: category.subTitle ?? '',
        subTitleTranslations: category.subTitleTranslations,
        lockedItems: _items.filter(_ => !_.isCustomerEditable).length,
        items: _items.map(_ => {
          const quantity = _.isOverridden ? _.quantity : _.defaultQuantity
          const item = merge(FormEquipmentItemSchema.getDefault(), {
            inventoryItemId: _.inventoryItemId,
            isCustomerEditable: _.isCustomerEditable,
            isOverridden: _.isOverridden,
            quantityMax: _.quantityMax,
            quantityMin: _.quantityMin,
            quantity: quantity,
            value: quantity,
            title: _.title,
          })
          return item
        }),
      };
    });

  return categories;
};

export const transformAllEquipmentProfilesToFormData = (equipmentProfiles?: EquipmentProfileItemDto[]) => {
  const mapProfilesByPrefix = groupBy(equipmentProfiles, _ => _.key?.split('.')[1] ?? '');

  return Object.entries(mapProfilesByPrefix).flatMap(([subPage, items]) => {
    const sortItemsByTitle = orderBy(items, ['title'], ['asc']);
    const mapItemsByCategory = groupBy(sortItemsByTitle, _ => _.category?.id);
    const sections = sortBy(values(mapItemsByCategory), _items => _items[0].category?.seq);

    return sections.filter(_items => !!_items[0]?.category).map(_items => {
      const category = _items[0].category!;
      return {
        categoryId: category.id,
        subPage,
        title: category.title ?? '',
        titleTranslations: category.titleTranslations,
        subTitle: category.subTitle ?? '',
        subTitleTranslations: category.subTitleTranslations,
        lockedItems: _items.filter(_ => !_.isCustomerEditable).length,
        items: _items.map(_ => {
          const quantity = _.isOverridden ? _.quantity : _.defaultQuantity;
          return merge(FormEquipmentItemSchema.getDefault(), {
            inventoryItemId: _.inventoryItemId,
            isCustomerEditable: _.isCustomerEditable,
            isOverridden: _.isOverridden,
            quantityMax: _.quantityMax,
            quantityMin: _.quantityMin,
            quantity,
            value: quantity,
            title: _.title,
          });
        }),
      };
    });
  });
};

export const useFormEquipmentProfile = (props?: FormEquipmentProfileProps): FormEquipmentProfileReturn => {
  return useForm<FormEquipmentProfile>({
    defaultValues: merge(FormEquipmentProfileSchema.getDefault(), props?.defaultValues),
    resolver: yupResolver(FormEquipmentProfileSchema),
  });
};
